/**
 * 현장 CCTV 뷰어 위젯 (S_cctv1 뷰어 로직 기반, WebSDK 전용)
 * - 대시보드(Dashboard) / 상황판(SiteDashboard) 공용
 * - divisions: 화면 분할 수 (1 = 1분할, 4 = 4분할)
 * - 1분할: 카메라 선택 SelectBox + 단일 화면
 * - 다분할: 분할 그리드 + 하단 페이지 이동(분할 버튼은 위젯별 고정이라 숨김)
 * - viewOnly(상황판): 조작 UI 없이 즉시 자동 재생
 */
import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { PulseLoader } from 'react-spinners';
import { useRecoilState, useRecoilValue } from 'recoil';
import { useTranslation } from 'react-i18next';

import { asideFoldState, userState } from '../../atoms';
import { apiGet } from '../../services/_common';
import { isMobile } from '../../utils/checkMobile';
import * as cctv from '../../utils/cctv';
import WebSdk from '../../pages/s_cctv/WebSdk';
import SelectBox from '../SelectBox';
import Portal from '../Portal';
import WebSdkModal from '../Modal/WebSdkModal';
import notSupport from '../../assets/images/illustration/78.svg';

interface ICameraObject {
  wsNum: string;
  nName: string;
  cName: string;
}

interface IProps {
  name?: string; // 위젯 타이틀 (dName)
  divisions?: number; // 화면 분할 수 (1, 4 ...)
  openModal?: any; // (선택) 상위에서 관리하는 모달 상태
  setOpenModal?: any; // (선택) 상위 모달 상태 변경 함수
  refetchInterval?: number; // (선택) 카메라 목록 자동 갱신 주기(ms)
  viewOnly?: boolean; // 상황판 상시 노출용 - 조작 UI 없이 자동재생만 (SelectBox/재생버튼/컨트롤러 숨김)
}

const CctvViewer = ({ name, divisions = 1, openModal, setOpenModal, refetchInterval, viewOnly = false }: IProps) => {
  const userInfo = useRecoilValue(userState);
  const mobile = isMobile();
  const [foldMenu] = useRecoilState<boolean>(asideFoldState);
  const { t } = useTranslation();

  const [cctvDivisions] = useState(divisions);
  const [currentPage, setCurrentPage] = useState(1);
  const [cameraInfo, setCameraInfo] = useState<ICameraObject[]>([]);
  const [nvrCdList, setNvrCdList] = useState([]);
  const [selectedCam, setSelectedCam] = useState<any>({ subCd: 0, cdName: '' });
  const [webSdkPlay, setWebSdkPlay] = useState(false);
  const [playBtn, setPlayBtn] = useState(true);
  // 상위에서 모달 상태를 내려주지 않은 경우(상황판 등) 내부 상태로 대체
  const [internalModal, setInternalModal] = useState<any>({ status: false, type: '', code: '', index: 0 });
  const modal = openModal ?? internalModal;
  const setModal = setOpenModal ?? setInternalModal;

  const cctvElement = useRef<HTMLDivElement>(null);

  const isSingle = cctvDivisions === 1; // 1분할 여부 (SelectBox 사용)
  // WebSDK는 분할 수를 가로/세로 한 변의 칸 수(n x n)로 받음: 1분할→1, 4분할→2, 9분할→3, 16분할→4
  const webSdkDivisionCount = Math.round(Math.sqrt(cctvDivisions));

  const onScroll = (e: any) => {
    if (e.target.className?.includes('contents dashboard')) {
      const top = cctvElement?.current?.getBoundingClientRect();
      if (cctv.getPluginOBJECT()?.oPlugin) {
        cctv.destroy();
        setWebSdkPlay(false);
      }
      if (top && top.top > 0) {
        setPlayBtn(true);
      } else {
        setPlayBtn(false);
      }
    }
  };

  useEffect(() => {
    window.addEventListener('scroll', (e) => onScroll(e), true);
    return () => {
      window.removeEventListener('scroll', (e) => onScroll(e), true);
    };
  }, []);

  useEffect(() => {
    if (cctv.getPluginOBJECT()?.oPlugin) {
      cctv.destroy();
      setWebSdkPlay(false);
    }
    setPlayBtn(true);
  }, [foldMenu]);

  const siteCamQuery: any = useQuery(['siteCameraGet', userInfo.hCd, userInfo.sCd], () => apiGet({ path: '/cam/site', req: { hCd: userInfo.hCd, sCd: userInfo.sCd } }), {
    retry: 3,
    enabled: !!userInfo.hCd && !!userInfo.sCd,
    ...(refetchInterval ? { refetchInterval } : {}),
  });

  useEffect(() => {
    if (siteCamQuery.isSuccess && siteCamQuery.data && siteCamQuery.data.data.statusCode === 200) {
      const { cameraList } = siteCamQuery.data.data.data;
      if (cameraList.length > 0) {
        setCameraInfo(cameraList);
        const newArray = cameraList.map((el: any, index: number) => {
          return { ...el, name: `${el.nName} ${el.cName}`, cdName: `${el.nName} ${el.cName}`, subCd: index, type: 'subCd' };
        });
        setSelectedCam(newArray[0]);
        setNvrCdList(newArray);
      }
    }
  }, [siteCamQuery.isSuccess, siteCamQuery.isFetching]);

  // 1분할에서만 SelectBox로 카메라(=페이지) 직접 이동
  useEffect(() => {
    if (isSingle) setCurrentPage(selectedCam.subCd + 1);
  }, [selectedCam]);

  const onClickRefresh = () => {
    if (cctv.getPluginOBJECT()?.oPlugin) {
      cctv.destroy();
    }
    siteCamQuery.refetch();
  };

  const onClickPlay = () => {
    setWebSdkPlay(true);
    setPlayBtn(false);
  };

  if (!siteCamQuery.isSuccess || siteCamQuery.data.status !== 200 || siteCamQuery.isRefetching) {
    return (
      <div className='centered-content'>
        <PulseLoader color='rgb(0, 122, 255)' size='10px' />
      </div>
    );
  }

  return (
    <>
      <div className='cctvNarrow'>
        {/* 상황판(viewOnly)은 헤더 없이 영상이 셀 전체를 채움 */}
        {!viewOnly && (
          <div className='widget-header'>
            <div className='widget-title flex-between'>
              <span>{name ? t(name) : `${t('현장 CCTV')} (${cctvDivisions}${t('분할')})`}</span>
              <div className='refresh-btn' onClick={onClickRefresh} role='presentation'>
                <span className='material-symbols-rounded'>refresh</span>
              </div>
            </div>
          </div>
        )}
        {mobile ? (
          <div className='emptyData'>
            <img src={notSupport} alt='' />
            <p className='main_text text-center'>{t('모바일 환경에서 지원되지 않는 화면입니다')}</p>
          </div>
        ) : cameraInfo?.length > 0 ? (
          <div className={`widget-body ${viewOnly ? 'cctv-view' : isSingle ? 'cctv-narrow' : 'cctv-grid'}`} ref={cctvElement}>
            {webSdkPlay || viewOnly ? (
              // 상황판(viewOnly)은 재생버튼 없이 즉시 자동 재생
              <>
                {isSingle && !viewOnly && (
                  <SelectBox options={nvrCdList} defaultOption={selectedCam.cdName} state={selectedCam} setState={setSelectedCam} stateKey='subCd' isWebSdk hideWebSdk optionHeight='height-sm' />
                )}
                <WebSdk
                  divisionCount={webSdkDivisionCount}
                  currentPageNumber={currentPage}
                  setCurrentPageNumber={setCurrentPage}
                  height={viewOnly ? '100%' : isSingle ? '17.5rem' : '18.5rem'}
                  dashboard
                  fitToContainer
                  hideSelectBorder
                />
              </>
            ) : (
              <>
                {isSingle && <SelectBox options={nvrCdList} defaultOption={selectedCam.cdName} state={selectedCam} setState={setSelectedCam} stateKey='subCd' isWebSdk optionHeight='height-sm' />}
                {playBtn && (
                  <div className='play-btn cctv' onClick={onClickPlay} role='presentation'>
                    <span className='material-symbols-rounded'>play_arrow</span>
                  </div>
                )}
              </>
            )}
          </div>
        ) : null}
      </div>
      <Portal openModal={modal?.status}>{modal && <WebSdkModal openModal={modal} setOpenModal={setModal} />}</Portal>
    </>
  );
};

export default CctvViewer;
